import { useCallback, useEffect, useMemo, useRef, useState, type ComponentProps } from "react";
import {
  Flex,
  LoadingIndicator,
  OverlayStyle,
  SlideoutDrawerV2,
  Tab,
  Tabs,
  Tag,
  Text,
  toast,
} from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { useAppContext } from "~/contexts/appContext";
import {
  useDataPackageImpactQuery,
  useDataPackageUsageQuery,
  useGetFormulasQuery,
  useGetQueriesQuery,
} from "~/graphql/supergraphHooks";
import {
  fromVersionStatus,
  type TemplateStatus,
} from "~/pages/Reporting/components/TemplateStatusBadge";
import { useDataStructureRoutingContext } from "~/pages/Reporting/DataStructureRoutingContext";
import { useDateFormatterWithTimezone } from "~/pages/UserAndRoles/utils/dateFormatter";
import { SPACING } from "~/styles/variables";
import { hasGlobalReportingAccess } from "~/utils/userRoles";
import { CreateQueryModal, type CreateQueryFormValues } from "../CreateQueryModal";
import { DataPackageTakeover } from "../DataPackageTakeover";
import {
  EditDataPackageSettingsModal,
  type EditDataPackageSettingsFormValues,
} from "../EditDataPackageSettingsModal";
import { CannotArchiveModal } from "./CannotArchiveModal";
import { ConfirmArchiveModal } from "./ConfirmArchiveModal";
import { DetailsPanel } from "./DetailsPanel";
import { FormulasPanel } from "./FormulasPanel";
import { FormulaTakeover } from "./FormulaTakeover";
import { HierarchyPanel } from "./HierarchyPanel";
import { QueriesPanel } from "./QueriesPanel";
import { QueryDetailTakeover } from "./QueryDetailTakeover";
import { safeRefetch } from "./safeRefetch";
import * as styles from "./styles_vanilla.css";
import { useDataPackageMutations } from "./useDataPackageMutations";
import { VersionHistoryTakeover } from "./VersionHistoryTakeover";
import type { PackageTab } from "../types";
import type { Query } from "./QueriesGrid";
import type { NewQuerySeed } from "./QueryDetailTakeover/QuerySettingsPanel/types";
import type { QueryUsageResolution, QueryUsageTemplate } from "./QueryDetailTakeover/types";
import type { DataPackageImpactQuery } from "~/graphql/supergraphTypes";

type HierarchyPanelProps = ComponentProps<typeof HierarchyPanel>;

type ImpactTemplate = DataPackageImpactQuery["dataPackageImpact"][number]["templates"][number];

const toTemplateStatus = (template: ImpactTemplate): TemplateStatus => {
  if (!template.isActive) return "archived";
  const versionStatus = (() => {
    if (template.currentVersion) return fromVersionStatus(template.currentVersion.status);
    // No current (approved) version: the template matched the impact via a
    // pending or denied version. The latest upload-complete version is the
    // template's head state and tells the two apart.
    const headVersion = template.versions
      .filter((version) => version.isUploadComplete)
      .reduce<ImpactTemplate["versions"][number] | null>(
        (latest, version) =>
          latest === null || version.createdAt > latest.createdAt ? version : latest,
        null,
      );
    return headVersion ? fromVersionStatus(headVersion.status) : "pending";
  })();
  // System templates (arenaId null) don't go through client authorization,
  // so an approved one reads "Active" rather than "Approved for use".
  const isSystemTemplate = template.arenaId === null;
  return versionStatus === "approved" && isSystemTemplate ? "active" : versionStatus;
};

/** Deduped while visible: repeated impact-query failures (e.g. after a
 * retry) re-toast only after the previous toast is dismissed. */
const IMPACT_ERROR_TOAST_ID = "data-package-impact-error";

const BASE_PACKAGE_TAB_CONFIG: { id: PackageTab; label: string }[] = [
  { id: "hierarchy", label: "Hierarchy" },
  { id: "formulas", label: "Formulas" },
  { id: "queries", label: "Queries" },
];

type DataPackageSlideoutProps = {
  isOpen: boolean;
  onClose: () => void;
  arenaId: number | null;
  /** Display name for the current arena; labels same-arena templates in the
   * query in-use warning. */
  arenaName?: string;
  dataPackageId: number | null;
  isDefault: boolean;
  onArchiveSuccess: () => void;
  onDuplicate: () => void;
  onDataPackageChanged: () => void;
};

export const DataPackageSlideout = ({
  isOpen,
  onClose,
  arenaId,
  arenaName,
  dataPackageId,
  onArchiveSuccess,
  onDuplicate,
  onDataPackageChanged,
}: DataPackageSlideoutProps) => {
  const { user } = useAppContext();
  const canAccessGlobalSettings = hasGlobalReportingAccess(user.roles);
  const {
    packageTab,
    formulaId,
    queryId,
    formulaViewFilter,
    handleFormulaViewFilterChange,
    queryViewFilter,
    handleQueryViewFilterChange,
    isEditDataPackage,
    isNewFormula,
    isEditFormula,
    isQueryDetail,
    isQueryEdit,
    isNewQuery,
    newQueryName,
    newQueryTemplateType,
    navigateToList,
    navigateToEditDataPackage,
    navigateToNewFormula,
    navigateToEditFormula,
    navigateToQueryDetail,
    navigateToNewQuery,
    navigateToQueriesTab,
    navigateToEditQuery,
    navigateToPackageTab,
    replaceToViewQuery,
  } = useDataStructureRoutingContext();
  const [isEditSettingsOpen, setIsEditSettingsOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [isCreateQueryModalOpen, setIsCreateQueryModalOpen] = useState(false);
  const [newQuerySeed, setNewQuerySeed] = useState<NewQuerySeed | null>(null);
  // The takeover mode follows the URL: `/queries/:id` is read-only view, and
  // `/queries/:id/edit` is the editing experience. Keeping this as a dedicated
  // route (instead of e.g. a `?mode=edit` query param) lets us gate the edit
  // URL with a permission check at the route level in the future.
  const isQueryViewMode = !isQueryEdit;

  const isFormulaTakeoverOpen = isNewFormula || isEditFormula;

  const {
    dataPackage,
    isDataPackageLoading,
    refetchDataPackage: refetch,
    saveMetadata,
    isSavingSettings,
    archive,
    isArchiving,
    restore,
    isRestoring,
    download,
    isExporting,
  } = useDataPackageMutations({ arenaId, dataPackageId, onDataPackageChanged });

  const dataPackageVersionId = dataPackage?.currentVersion?.id ?? null;

  const { data: formulasData, refetch: refetchFormulas } = useGetFormulasQuery({
    variables: {
      arenaId,
      dataPackageVersionId: dataPackageVersionId!,
      includeInactive: true,
    },
    skip: !dataPackageVersionId,
    fetchPolicy: "cache-and-network",
  });

  const { data: queriesData } = useGetQueriesQuery({
    variables: {
      arenaId,
      includeInactive: true,
    },
    skip: !isOpen,
    fetchPolicy: "cache-and-network",
  });

  const { data: usageData, loading: isUsageLoading } = useDataPackageUsageQuery({
    variables: { dataPackageIds: [dataPackageId!] },
    skip: !dataPackageId,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: impactData,
    loading: isImpactLoading,
    error: impactError,
    refetch: refetchImpact,
  } = useDataPackageImpactQuery({
    variables: { dataPackageVersionId: dataPackageVersionId!, arenaId },
    skip: !dataPackageVersionId,
    fetchPolicy: "cache-and-network",
  });

  // Query IDs referenced by at least one active template. These queries
  // cannot be archived. While usage is still resolving we cannot
  // prove a query is safe to archive, so callers should treat the unresolved
  // state as blocking.
  const isQueryUsageResolved = !isImpactLoading && !!impactData;
  // Tri-state for the takeover so a failed lookup reads as an actionable
  // error (tooltip + retry) rather than an indefinite "loading". A refetch
  // error after a successful fetch keeps the last good data and stays
  // "resolved".
  const queryUsageResolution: QueryUsageResolution = (() => {
    if (isQueryUsageResolved) return "resolved";
    return impactError && !isImpactLoading ? "error" : "loading";
  })();

  useEffect(() => {
    if (!impactError) return;
    toast.error(
      "Failed to check which templates use this package's queries. Archiving and updating queries is unavailable until it loads.",
      { toastId: IMPACT_ERROR_TOAST_ID },
    );
  }, [impactError]);

  const handleRetryUsageResolution = useCallback(() => {
    void refetchImpact();
  }, [refetchImpact]);
  const queryIdsInUseByActiveTemplates = useMemo(
    () =>
      new Set(
        (impactData?.dataPackageImpact ?? [])
          .filter((impact) => impact.templates.some((template) => template.isActive))
          .map((impact) => impact.queryId),
      ),
    [impactData],
  );

  const activeTemplateCount =
    usageData?.dataPackageUsage?.find((u) => u.dataPackageId === dataPackageId)
      ?.activeTemplateCount ?? 0;

  const isDataPackageArchived = dataPackage?.isActive === false;
  const isSystemPackageInArenaView = arenaId !== null && dataPackage?.arenaId === null;
  const { formatDateWithTimezone } = useDateFormatterWithTimezone();
  const createdDateFormatted = dataPackage?.createdAt
    ? formatDateWithTimezone(dataPackage.createdAt, "MMM d, yyyy h:mm a")
    : "";
  const archivedDateFormatted = dataPackage?.archivedAt
    ? formatDateWithTimezone(dataPackage.archivedAt, "MMM d, yyyy h:mm a")
    : "";
  const archivedBy = dataPackage?.archivedByUser?.fullName?.trim() || null;

  const isUsageResolved = !isUsageLoading && !!usageData;
  const isInUse = !isUsageResolved || activeTemplateCount > 0;

  const formulas = useMemo(() => {
    if (!formulasData?.finpubFormulas) {
      return [];
    }

    return formulasData.finpubFormulas.map((formula) => ({
      id: String(formula.id),
      name: formula.name,
      description: formula.description ?? "",
      formula: formula.expressionWithLabels,
      isActive: formula.isActive,
      lastUpdated: formula.updatedAt ?? null,
      updatedByFullName: formula.updatedByUser?.fullName?.trim() || null,
      archivedAt: formula.archivedAt ?? null,
      archivedByFullName: formula.archivedByUser?.fullName?.trim() || null,
    }));
  }, [formulasData]);

  const queries = useMemo<Query[]>(
    () =>
      (queriesData?.finpubQueries ?? [])
        .filter(
          (query) =>
            dataPackageVersionId !== null &&
            query.dataPackageVersionId === dataPackageVersionId,
        )
        .map<Query>((query) => ({
          id: String(query.id),
          name: query.name,
          numberOfFields: query.fieldCount,
          isActive: query.isActive,
          lastUpdated: query.updatedAt ?? null,
          updatedByFullName:
            query.updatedByUser?.fullName?.trim() ||
            query.createdByUser?.fullName?.trim() ||
            null,
          archivedAt: query.archivedAt ?? null,
          archivedByFullName: query.archivedByUser?.fullName?.trim() || null,
        })),
    [queriesData, dataPackageVersionId],
  );

  const handleOpenEditSettings = useCallback(() => {
    setIsEditSettingsOpen(true);
  }, []);

  const handleCloseEditSettings = useCallback(() => {
    setIsEditSettingsOpen(false);
  }, []);

  const handleSaveSettings = useCallback(
    async (values: EditDataPackageSettingsFormValues) => {
      const ok = await saveMetadata({
        name: values.name,
        description: values.description,
      });
      if (ok) handleCloseEditSettings();
    },
    [handleCloseEditSettings, saveMetadata],
  );

  const handleOpenUpdateHierarchy = useCallback(() => {
    if (!dataPackageId) return;
    navigateToEditDataPackage(dataPackageId);
  }, [dataPackageId, navigateToEditDataPackage]);

  const handleCloseUpdateHierarchy = useCallback(() => {
    if (dataPackageId) {
      navigateToPackageTab(dataPackageId, "hierarchy");
    }
  }, [dataPackageId, navigateToPackageTab]);

  const handleOpenGlobalSettings = useCallback(() => {
    window.open("/canopy/reporting/settings", "_blank", "noopener,noreferrer");
  }, []);

  const handleOpenNewFormula = useCallback(() => {
    if (!dataPackageVersionId || !dataPackageId) {
      toast.error("Data package is not fully loaded. Please try again.");
      return;
    }
    navigateToNewFormula(dataPackageId);
  }, [dataPackageVersionId, dataPackageId, navigateToNewFormula]);

  const handleOpenEditFormula = useCallback(
    (fId: string) => {
      if (!dataPackageVersionId || !dataPackageId) {
        toast.error("Data package is not fully loaded. Please try again.");
        return;
      }
      navigateToEditFormula(dataPackageId, Number(fId));
    },
    [dataPackageVersionId, dataPackageId, navigateToEditFormula],
  );

  useEffect(() => {
    if (!isFormulaTakeoverOpen || isDataPackageLoading || !dataPackageId) return;

    if (isDataPackageArchived) {
      toast.error(
        isEditFormula
          ? "Archived formulas cannot be edited"
          : "Cannot add formulas to an archived data package",
      );
      navigateToPackageTab(dataPackageId, "formulas");
      return;
    }

    if (isSystemPackageInArenaView) {
      toast.error("System formulas can only be managed from global settings");
      navigateToPackageTab(dataPackageId, "formulas");
    }
  }, [
    isFormulaTakeoverOpen,
    isEditFormula,
    isDataPackageLoading,
    isDataPackageArchived,
    isSystemPackageInArenaView,
    dataPackageId,
    navigateToPackageTab,
  ]);

  useEffect(() => {
    if (!isQueryDetail || isDataPackageLoading || !dataPackageId) return;

    if (isDataPackageArchived && isQueryEdit) {
      toast.error("Archived queries cannot be edited");
      navigateToQueriesTab(dataPackageId);
      return;
    }
  }, [
    isQueryDetail,
    isQueryEdit,
    isDataPackageLoading,
    isDataPackageArchived,
    dataPackageId,
    navigateToQueriesTab,
  ]);

  // One-shot latch so re-renders (or refetch-driven effect re-runs) can't fire
  // the toast + redirect repeatedly while the guard condition stays true. Reset
  // once the condition clears so a later re-entry redirects again.
  const hasRedirectedSystemQueryRef = useRef(false);
  useEffect(() => {
    if (!isQueryDetail || isDataPackageLoading || !dataPackageId) return;

    if (!(isQueryEdit && isSystemPackageInArenaView)) {
      hasRedirectedSystemQueryRef.current = false;
      return;
    }
    if (hasRedirectedSystemQueryRef.current) return;
    hasRedirectedSystemQueryRef.current = true;
    toast.error("System queries can only be managed from global settings");
    navigateToQueriesTab(dataPackageId);
  }, [
    isQueryDetail,
    isQueryEdit,
    isDataPackageLoading,
    isSystemPackageInArenaView,
    dataPackageId,
    navigateToQueriesTab,
  ]);

  const handleCloseFormulaTakeover = useCallback(() => {
    if (dataPackageId) {
      navigateToPackageTab(dataPackageId, "formulas");
    }
  }, [dataPackageId, navigateToPackageTab]);

  const handleOpenNewQuery = useCallback(() => {
    setIsCreateQueryModalOpen(true);
  }, []);

  const handleCloseCreateQueryModal = useCallback(() => {
    setIsCreateQueryModalOpen(false);
  }, []);

  const handleCloseQueryDetail = useCallback(() => {
    setNewQuerySeed(null);
    if (dataPackageId) {
      navigateToQueriesTab(dataPackageId);
    }
  }, [dataPackageId, navigateToQueriesTab]);

  const handleOpenQueryDetail = useCallback(
    (qId: string) => {
      if (!dataPackageId) return;
      const numericId = Number(qId);
      if (Number.isNaN(numericId)) return;
      navigateToQueryDetail(dataPackageId, numericId);
    },
    [dataPackageId, navigateToQueryDetail],
  );

  // Deferred-create flow: only collects the name and template type. The actual
  // `finpubQueryCreate` fires from the takeover's Save button so
  // abandoning the takeover doesn't orphan an empty row. The name and template
  // type survive the route transition via `?name=` and `?templateType=` params.
  const handleCreateQuery = useCallback(
    (values: CreateQueryFormValues): Promise<void> => {
      if (!dataPackageId || !dataPackageVersionId) {
        toast.error("Data package is not fully loaded. Please try again.");
        return Promise.resolve();
      }
      setNewQuerySeed({
        supportedTemplateType: values.supportedTemplateType,
        supportedTransactionTypes: values.supportedTransactionTypes,
      });
      setIsCreateQueryModalOpen(false);
      navigateToNewQuery(dataPackageId, values.name, values.supportedTemplateType);
      return Promise.resolve();
    },
    [dataPackageId, dataPackageVersionId, navigateToNewQuery],
  );

  const activeQuery = useMemo(
    () => (queryId !== null ? queries.find((q) => q.id === String(queryId)) : undefined),
    [queryId, queries],
  );

  // For new-query the name lives on the URL; for view/edit it comes from
  // the `queries` list. `"Untitled query"` is the placeholder while the
  // list is in flight after a deep-link.
  const activeQueryName = isNewQuery
    ? (newQueryName ?? "Untitled query")
    : (activeQuery?.name ?? (queryId === null ? "" : "Untitled query"));

  // A query is "in use" when it's referenced by at least one active template.
  // This gates the archive block; the edit-time in-use warning keys off
  // `activeQueryUsageTemplates` instead (which also includes archived and
  // denied templates). Keyed off the routing `queryId` (not `activeQuery`,
  // which is undefined while the queries list is in flight) and fails closed
  // while usage is unresolved, so an in-use query can never slip through
  // archiving before `dataPackageImpact` lands. The takeover additionally
  // disables the archive action and the Update button until
  // `usageResolution` is "resolved", so the unresolved state reads as
  // "loading" rather than a wrong cannot-archive modal or a skipped in-use
  // warning.
  const activeQueryIsInUse =
    !isQueryUsageResolved || (queryId !== null && queryIdsInUseByActiveTemplates.has(queryId));

  // Templates shown in the edit-time "Query is in use" warning, from the same
  // `dataPackageImpact` data that drives the archive gating.
  const activeQueryUsageTemplates = useMemo<QueryUsageTemplate[]>(() => {
    if (queryId === null) return [];
    const impact = impactData?.dataPackageImpact.find((i) => i.queryId === queryId);
    return (impact?.templates ?? []).map((template) => ({
      id: String(template.id),
      templateName: template.name,
      // The backend enriches impact templates with `arenaName`; the current
      // arena's name and "Client" are fallbacks in case that enrichment
      // failed (it degrades to null rather than erroring).
      clientOrSystem:
        template.arenaId === null
          ? "System"
          : (template.arenaName ??
            (template.arenaId === arenaId && arenaName ? arenaName : "Client")),
      status: toTemplateStatus(template),
    }));
  }, [queryId, impactData, arenaId, arenaName]);

  const canRequestEditQuery =
    dataPackageId !== null &&
    queryId !== null &&
    !isSystemPackageInArenaView &&
    !isDataPackageArchived;
  const canMutateQuery = !isSystemPackageInArenaView && !isDataPackageArchived;
  const handleRequestEditQuery = useCallback(() => {
    if (dataPackageId === null || queryId === null) return;
    navigateToEditQuery(dataPackageId, queryId);
  }, [dataPackageId, queryId, navigateToEditQuery]);

  const handleCancelEditQuery = useCallback(() => {
    if (dataPackageId === null || queryId === null) return;
    replaceToViewQuery(dataPackageId, queryId);
  }, [dataPackageId, queryId, replaceToViewQuery]);

  // After Save succeeds (create or update), drop the user on the read-only
  // view route. `replace` keeps Back off the stale `/new` or `/edit` URL.
  const handleQuerySaved = useCallback(
    (savedQueryId: number) => {
      if (dataPackageId === null) return;
      replaceToViewQuery(dataPackageId, savedQueryId);
    },
    [dataPackageId, replaceToViewQuery],
  );

  // The takeover invokes `onArchive` after a successful archive; the parent's
  // job is to navigate back to the queries tab. We intentionally do NOT also
  // call `navigateToQueriesTab` from inside `onExit` here — the takeover only
  // calls `onExit` when no archive happened, so the navigation only fires once.
  const handleQueryArchived = useCallback(() => {
    if (dataPackageId) {
      navigateToQueriesTab(dataPackageId);
    }
  }, [dataPackageId, navigateToQueriesTab]);

  const handleQueryRestored = useCallback(() => {
    if (dataPackageId) {
      navigateToQueriesTab(dataPackageId);
    }
  }, [dataPackageId, navigateToQueriesTab]);

  const handleQueryMutated = useCallback(() => {
    onDataPackageChanged();
  }, [onDataPackageChanged]);

  // Lower-cased for `CreateQueryModal`'s case-insensitive duplicate check.
  // Use the current package's query list so duplicate names are allowed across
  // different data packages.
  const existingQueryNames = useMemo(
    () => new Set(queries.map((q) => q.name.trim().toLowerCase())),
    [queries],
  );

  const handleFormulaCreated = useCallback(async () => {
    await safeRefetch(refetchFormulas, "formulas");
    await safeRefetch(refetch, "data package");
    onDataPackageChanged();
  }, [refetchFormulas, refetch, onDataPackageChanged]);

  const handleOpenArchiveModal = useCallback(() => {
    setIsArchiveModalOpen(true);
  }, []);

  const handleCloseArchiveModal = useCallback(() => {
    setIsArchiveModalOpen(false);
  }, []);

  const handleConfirmArchive = useCallback(async () => {
    const ok = await archive();
    if (ok) {
      setIsArchiveModalOpen(false);
      navigateToList();
      onArchiveSuccess();
    }
  }, [archive, navigateToList, onArchiveSuccess]);

  const handleRestore = useCallback(async () => {
    const ok = await restore();
    if (ok) navigateToList();
  }, [navigateToList, restore]);

  const menuItems = useMemo(() => {
    if (isDataPackageArchived) {
      return [
        {
          label: "Restore data package",
          onClick: handleRestore,
          isDisabled: isRestoring,
        },
      ];
    }

    if (isSystemPackageInArenaView) {
      const items = [
        {
          label: "Duplicate data package",
          onClick: onDuplicate,
        },
      ];
      if (canAccessGlobalSettings) {
        items.push({
          label: "Update in global settings",
          onClick: handleOpenGlobalSettings,
        });
      }
      return items;
    }

    return [
      {
        label: "Duplicate data package",
        onClick: onDuplicate,
      },
      {
        label: "Edit settings",
        onClick: handleOpenEditSettings,
        hasDivider: true,
      },
      {
        label: "Archive data package",
        onClick: handleOpenArchiveModal,
        color: Colors.Danger600,
        isDisabled: !isUsageResolved,
      },
    ];
  }, [
    isDataPackageArchived,
    isRestoring,
    handleRestore,
    isSystemPackageInArenaView,
    canAccessGlobalSettings,
    handleOpenGlobalSettings,
    onDuplicate,
    handleOpenEditSettings,
    handleOpenArchiveModal,
    isUsageResolved,
  ]);

  const handleDataPackageEdited = useCallback(async () => {
    await safeRefetch(refetch, "data package");
    onDataPackageChanged();
  }, [refetch, onDataPackageChanged]);

  const handleTabClick = useCallback(
    (tab: PackageTab) => {
      if (dataPackageId) navigateToPackageTab(dataPackageId, tab);
    },
    [dataPackageId, navigateToPackageTab],
  );

  if (!dataPackageId) {
    return null;
  }

  const createdBy = dataPackage?.createdByUser?.fullName?.trim() || null;

  return (
    <>
      <SlideoutDrawerV2
        isOpen={
          isOpen &&
          !isVersionHistoryOpen &&
          !isFormulaTakeoverOpen &&
          !isEditDataPackage &&
          !isQueryDetail
        }
        onClose={onClose}
        isDismissable
        overlayStyle={OverlayStyle.Opaque}
        data-testid="data-package-slideout"
      >
        <SlideoutDrawerV2.ComplexHeader
          title={dataPackage?.name ?? "Loading..."}
          titleAdornment={
            isDataPackageArchived ? <Tag label="Archived" variant="neutral" /> : undefined
          }
          subtitles={[
            <Text key="description" variant="secondary-md" asSpan>
              {dataPackage?.description ?? ""}
            </Text>,
          ]}
          showGap={false}
          showBorder={false}
          className={styles.slideoutHeader}
          renderMenu
          menuItems={menuItems}
          data-testid="slideout-complex-header"
        />
        <SlideoutDrawerV2.Body className={styles.drawerBody}>
          {isDataPackageLoading ? (
            <Flex alignItems="center" justifyContent="center" flexGrow={1}>
              <LoadingIndicator />
            </Flex>
          ) : (
            <Flex flexDirection="column" className={styles.container}>
              <Tabs key="tabs" className={styles.tabs}>
                {BASE_PACKAGE_TAB_CONFIG.map(({ id, label }) => (
                  <Tab
                    key={id}
                    data-testid={`${id}-tab`}
                    active={packageTab === id}
                    text={label}
                    onClick={() => handleTabClick(id)}
                  />
                ))}
              </Tabs>
              <Flex flexDirection="column" className={styles.tabContent}>
                {packageTab === "hierarchy" && (
                  <Flex flexDirection="column" gap={SPACING.MEDIUM}>
                    <DetailsPanel
                      title="Hierarchy details"
                      version={dataPackage?.version?.toFixed(1) ?? null}
                      createdDate={createdDateFormatted}
                      createdBy={createdBy}
                      archivedDate={isDataPackageArchived ? archivedDateFormatted : null}
                      archivedBy={isDataPackageArchived ? archivedBy : null}
                      onViewHistory={() => setIsVersionHistoryOpen(true)}
                    />
                    <HierarchyPanel
                      typeHierarchy={
                        dataPackage?.typeHierarchy as HierarchyPanelProps["typeHierarchy"]
                      }
                      onDownload={() => download()}
                      onUpdateHierarchy={handleOpenUpdateHierarchy}
                      isDownloading={isExporting}
                      hideUpdateAction={isDataPackageArchived || isSystemPackageInArenaView}
                    />
                  </Flex>
                )}
                {packageTab === "formulas" && (
                  <Flex flexDirection="column" gap={SPACING.MEDIUM}>
                    <FormulasPanel
                      formulas={formulas}
                      viewFilter={formulaViewFilter}
                      onViewFilterChange={handleFormulaViewFilterChange}
                      onEditFormula={
                        isDataPackageArchived || isSystemPackageInArenaView
                          ? undefined
                          : handleOpenEditFormula
                      }
                      onNewFormula={
                        isDataPackageArchived || isSystemPackageInArenaView
                          ? undefined
                          : handleOpenNewFormula
                      }
                      isDataPackageArchived={isDataPackageArchived}
                    />
                  </Flex>
                )}
                {packageTab === "queries" && (
                  <Flex flexDirection="column" gap={SPACING.MEDIUM}>
                    <QueriesPanel
                      queries={queries}
                      viewFilter={queryViewFilter}
                      onViewFilterChange={handleQueryViewFilterChange}
                      onEditQuery={handleOpenQueryDetail}
                      onNewQuery={
                        isDataPackageArchived || isSystemPackageInArenaView
                          ? undefined
                          : handleOpenNewQuery
                      }
                      isDataPackageArchived={isDataPackageArchived}
                    />
                  </Flex>
                )}
              </Flex>
            </Flex>
          )}
        </SlideoutDrawerV2.Body>
        {isInUse ? (
          <CannotArchiveModal isOpen={isArchiveModalOpen} onClose={handleCloseArchiveModal} />
        ) : (
          <ConfirmArchiveModal
            isOpen={isArchiveModalOpen}
            onClose={handleCloseArchiveModal}
            onConfirm={handleConfirmArchive}
            isArchiving={isArchiving}
          />
        )}
        {dataPackageId != null && (
          <EditDataPackageSettingsModal
            isOpen={isEditSettingsOpen}
            onClose={handleCloseEditSettings}
            onSave={handleSaveSettings}
            isSaving={isSavingSettings}
            initialValues={{
              name: dataPackage?.name ?? "",
              description: dataPackage?.description ?? "",
            }}
            arenaId={arenaId}
            dataPackageId={dataPackageId}
          />
        )}
        <CreateQueryModal
          isOpen={isCreateQueryModalOpen}
          onClose={handleCloseCreateQueryModal}
          onCreate={handleCreateQuery}
          isCreating={false}
          existingNames={existingQueryNames}
          typeHierarchy={dataPackage?.typeHierarchy}
        />
      </SlideoutDrawerV2>
      {isVersionHistoryOpen && (
        <VersionHistoryTakeover
          arenaId={arenaId}
          dataPackageId={dataPackageId}
          onExit={() => setIsVersionHistoryOpen(false)}
          onDownload={download}
          isDownloading={isExporting}
        />
      )}
      {isFormulaTakeoverOpen &&
        dataPackageVersionId !== null &&
        !isDataPackageArchived &&
        !isSystemPackageInArenaView && (
          <FormulaTakeover
            onExit={handleCloseFormulaTakeover}
            onSuccess={handleFormulaCreated}
            arenaId={arenaId}
            dataPackageVersionId={dataPackageVersionId}
            formulaId={formulaId}
            typeHierarchy={dataPackage?.typeHierarchy}
            formulas={formulas}
          />
        )}
      <DataPackageTakeover
        isOpen={isEditDataPackage}
        onClose={handleCloseUpdateHierarchy}
        onSuccess={handleDataPackageEdited}
        arenaId={arenaId}
        dataPackageId={dataPackageId}
        dataPackageVersionId={dataPackageVersionId}
      />
      {/* `dataPackage &&` gate avoids firing the takeover's
        `useFinpubQueryQuery` with a wrong-scope `arenaId: null` while the
        package is still loading — once it lands the takeover stays
        mounted across cache-and-network refetches because `dataPackage`
        stays defined. */}
      {isQueryDetail && dataPackage && (
        <QueryDetailTakeover
          queryName={activeQueryName}
          queryId={queryId}
          // Queries inherit the package's scope — passing the current
          // view's `arenaId` blows up `finpubQueryUpdate` when an arena
          // user is viewing a system package (system queries must be
          // mutated with `arenaId: null`).
          arenaId={dataPackage.arenaId ?? null}
          dataPackageVersionId={dataPackageVersionId}
          isNewQuery={isNewQuery}
          initialSupportedTemplateType={newQueryTemplateType ?? undefined}
          onExit={handleCloseQueryDetail}
          onCancelEdit={!isNewQuery ? handleCancelEditQuery : undefined}
          onQuerySaved={handleQuerySaved}
          isViewMode={isQueryViewMode}
          onRequestEdit={
            !isNewQuery && canRequestEditQuery ? handleRequestEditQuery : undefined
          }
          onArchive={isNewQuery || !canMutateQuery ? undefined : handleQueryArchived}
          onRestore={isNewQuery || !canMutateQuery ? undefined : handleQueryRestored}
          onQueryMutated={handleQueryMutated}
          isInUse={!isNewQuery && activeQueryIsInUse}
          usageResolution={queryUsageResolution}
          onRetryUsageResolution={handleRetryUsageResolution}
          usageTemplates={isNewQuery ? [] : activeQueryUsageTemplates}
          typeHierarchy={dataPackage?.typeHierarchy}
          formulas={formulas}
          isDataPackageArchived={isDataPackageArchived}
          newQuerySeed={isNewQuery ? newQuerySeed : null}
        />
      )}
    </>
  );
};
